<script setup lang="ts">
import { ref } from 'vue'
import StudentSettingsLayout from '@/Layouts/StudentSettingsLayout.vue'
import SavedCards from '@/Components/Payment/SavedCards.vue'
import UnpaidLessons from '@/Components/Payment/UnpaidLessons.vue'
import { CreditCard, Download, AlertCircle, Receipt } from 'lucide-vue-next'

interface Payment {
  id: number
  paid_at: string | null
  amount: string
  refunded: string
  net_paid: string
  is_refunded: boolean
  is_fully_refunded: boolean
  refunded_at: string | null
  currency: string
  status: string
  failure_reason: string | null
  tutor_name: string
  plan_name: string
  lesson_on: string | null
  stripe_invoice_id: string | null
}

interface Totals {
  charged: string
  refunded: string
  net: string
  has_refunds: boolean
}

interface UnpaidLesson {
  id: number
  tutor_name: string
  starts_at: string
  amount: string
  reason: string | null
}

defineProps<{
  unpaidLessons: UnpaidLesson[]
  creditBalance: string
  payments: Payment[]
  totals: Totals
  stripeKey: string
}>()

// Note: each list reads the cards once, on mount, so a change in one remounts the other.
const savedCardsVersion = ref(0)
const unpaidLessonsVersion = ref(0)

function getStatusClasses(status: string): string {
  switch (status) {
    case 'Succeeded':
      return 'bg-green-100 text-green-800'
    case 'Failed':
      return 'bg-red-100 text-red-800'
    case 'Pending':
      return 'bg-yellow-100 text-yellow-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}
</script>

<template>
  <StudentSettingsLayout>
    <div class="space-y-6">
      <!-- Cards first: this is the page both payment emails send people to, and
           they arrive wanting to fix a card rather than read a statement. -->
      <SavedCards :key="savedCardsVersion" :stripe-key="stripeKey" @changed="unpaidLessonsVersion++" />

      <UnpaidLessons
        :key="unpaidLessonsVersion"
        :lessons="unpaidLessons"
        :stripe-key="stripeKey"
        @changed="savedCardsVersion++"
      />

      <p v-if="Number(creditBalance) > 0" class="rounded-xl bg-green-50 px-4 py-3 text-sm text-green-800">
        You have £{{ creditBalance }} of credit. It is used for your next lessons before your card is charged, and is lost if your lessons end.
      </p>

      <!-- Payment History -->
      <section class="bg-white rounded-2xl shadow-sm p-8">
        <div class="flex items-center gap-3 mb-6">
          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <CreditCard class="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Billing & Payments</h2>
            <p class="text-gray-500 text-sm">View your payment history and download invoices</p>
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="payments.length === 0" class="text-center py-12">
          <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Receipt class="w-8 h-8 text-gray-400" />
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-1">No payments yet</h3>
          <p class="text-gray-500 text-sm">Your payment history will appear here once you subscribe to a tutor.</p>
        </div>

        <!-- Totals: what was charged, what came back, what it actually cost -->
        <div v-else-if="totals.has_refunds" class="grid grid-cols-3 gap-4 mb-6">
          <div class="rounded-xl border border-gray-200 p-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Charged</p>
            <p class="mt-1 text-2xl font-semibold text-gray-900">&pound;{{ totals.charged }}</p>
          </div>
          <div class="rounded-xl border border-gray-200 p-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">Refunded</p>
            <p class="mt-1 text-2xl font-semibold text-amber-600">&minus;&pound;{{ totals.refunded }}</p>
          </div>
          <div class="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider">You paid</p>
            <p class="mt-1 text-2xl font-semibold text-gray-900">&pound;{{ totals.net }}</p>
          </div>
        </div>

        <!-- Payments Table -->
        <div v-if="payments.length > 0" class="overflow-x-auto">
          <table class="w-full">
            <thead>
              <tr class="border-b border-gray-200">
                <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tutor</th>
                <th class="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">For</th>
                <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Charged</th>
                <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Refunded</th>
                <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">You paid</th>
                <th class="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th class="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="payment in payments" :key="payment.id" class="hover:bg-gray-50 transition-colors">
                <td class="py-4 px-4 text-sm text-gray-900">{{ payment.paid_at ?? 'Pending' }}</td>
                <td class="py-4 px-4 text-sm text-gray-900">{{ payment.tutor_name }}</td>
                <td class="py-4 px-4 text-sm text-gray-600">{{ payment.lesson_on ? `Lesson on ${payment.lesson_on}` : payment.plan_name }}</td>
                <td class="py-4 px-4 text-sm text-gray-600 text-right">&pound;{{ payment.amount }}</td>
                <td class="py-4 px-4 text-sm text-right">
                  <span v-if="payment.is_refunded" class="text-amber-600">
                    &minus;&pound;{{ payment.refunded }}
                    <span v-if="payment.refunded_at" class="block text-xs text-gray-400">{{ payment.refunded_at }}</span>
                  </span>
                  <span v-else class="text-gray-400">&mdash;</span>
                </td>
                <td class="py-4 px-4 text-sm text-gray-900 text-right font-medium">
                  &pound;{{ payment.net_paid }}
                  <span v-if="payment.is_fully_refunded" class="block text-xs font-normal text-amber-600">Fully refunded</span>
                </td>
                <td class="py-4 px-4 text-center">
                  <span
                    :class="getStatusClasses(payment.status)"
                    class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  >
                    {{ payment.status }}
                  </span>
                  <!-- Failure reason tooltip -->
                  <div v-if="payment.failure_reason" class="flex items-center justify-center mt-1 gap-1">
                    <AlertCircle class="w-3 h-3 text-red-400" />
                    <span class="text-xs text-red-500">{{ payment.failure_reason }}</span>
                  </div>
                </td>
                <td class="py-4 px-4 text-right">
                  <a
                    v-if="payment.status === 'Succeeded'"
                    :href="`/student/settings/billing/invoice/${payment.id}`"
                    target="_blank"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Download class="w-3.5 h-3.5" />
                    PDF
                  </a>
                  <span v-else class="text-xs text-gray-400">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </StudentSettingsLayout>
</template>
